import styled, { css, up } from "@xstyled/styled-components";
import { TargetIcon, ShowIcon, MegaphoneIcon } from "@welcome-ui/icons";

import { Card, Heading, Spacing, Text } from "@otta/design";
import { palette, pxToRem } from "@otta/design-tokens";
import { Icon } from "@otta/icons";
import { TooltipWithIcon } from "@hire/components/TooltipWithIcon";

const CardContainer = styled.div`
  display: flex;
  gap: md;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;

  ${up(
    "tablet",
    css`
      flex-wrap: nowrap;
    `
  )};
`;

const StyledCard = styled(Card)<{ backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  width: ${pxToRem(325)};
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const CandidateNotifiedDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: sm;
`;

const Tag = styled(Text).attrs({ size: -1, as: "span" })`
  padding: xs;
  border-radius: ${pxToRem(8)};
  background-color: green-200;
`;

const IconWrapper = styled.div`
  svg {
    width: 100px;
  }
`;

const formatNumber = (number?: number) =>
  number ? number.toLocaleString() : "N/A";

export function Overview({
  candidatePool,
  candidatesViewed,
  candidateNotified,
  candidateNotifiedLastWeek,
  firstPosted,
  lastRefreshed,
}: {
  candidatePool?: number;
  candidatesViewed?: number;
  candidateNotified?: number;
  candidateNotifiedLastWeek?: number;
  sourcingPool?: number;
  firstPosted: string | null;
  lastRefreshed: string | undefined;
}) {
  return (
    <Spacing>
      <Spacing>
        <Text bold size={2}>
          Overview
        </Text>
        {firstPosted ? (
          <Text>
            This job was posted on {firstPosted}. This information is updated
            daily. {!!lastRefreshed && <>Last updated on {lastRefreshed}.</>}
          </Text>
        ) : null}
      </Spacing>
      <CardContainer>
        {typeof candidatePool === "number" && candidatePool > 0 && (
          <DataCard
            value={formatNumber(candidatePool)}
            description="Matched candidates active in the last 12 weeks"
            image={
              <IconWrapper>
                <TargetIcon />
              </IconWrapper>
            }
            backgroundColor={palette.beige.shade200}
          />
        )}
        {typeof candidatesViewed === "number" && (
          <DataCard
            value={formatNumber(candidatesViewed)}
            description="Candidates viewed this job"
            image={
              <IconWrapper>
                <ShowIcon />
              </IconWrapper>
            }
            backgroundColor={palette.beige.shade200}
          />
        )}
        {typeof candidateNotified === "number" && (
          <DataCard
            value={formatNumber(candidateNotified)}
            description={
              <CandidateNotifiedDescription>
                <Tag>
                  <strong>+{formatNumber(candidateNotifiedLastWeek)}</strong>{" "}
                  from last week
                </Tag>
                <Text>
                  Candidates notified about your role{" "}
                  <TooltipWithIcon
                    content="Candidates receive push notifications or emails about new roles daily or weekly."
                    icon={<Icon icon="circle-info" />}
                  />
                </Text>
              </CandidateNotifiedDescription>
            }
            image={
              <IconWrapper>
                <MegaphoneIcon />
              </IconWrapper>
            }
            backgroundColor={palette.beige.shade200}
          />
        )}
      </CardContainer>
    </Spacing>
  );
}

const DataCard = ({
  value,
  description,
  image,
  backgroundColor,
}: {
  value: string;
  description: React.ReactNode;
  image: React.ReactNode;
  backgroundColor: string;
}) => {
  return (
    <StyledCard backgroundColor={backgroundColor}>
      <div>
        <Spacing>
          <Heading size={4}>{value}</Heading>
          {typeof description === "string" ? (
            <Text>{description}</Text>
          ) : (
            description
          )}
        </Spacing>
      </div>
      {image}
    </StyledCard>
  );
};
